/**
 * Client-side mirror of yaas-api `getStepRequires` / `getStepReadiness`
 * (`OnboardingStepService` + `stepPayloadSchemas/index.ts`). Keep in sync
 * when the BE dependency graph changes.
 */

import { APPLICANT_STEP_DISPLAY_LABEL } from "@solomon-apps/shared/src/onboarding/applicantReview";

import type {
    OnboardingApplicationStatus,
    OnboardingApplicationView,
    OnboardingStepName,
    OnboardingStepStatus,
    OnboardingStepView
} from "@/services/clientOnboarding";

import { rollupCompanySubsectionStatus } from "./OnboardingShared";
import type { SectionStatus, Slot, SlotStatus, SubsectionId } from "./types";

/** FE subsection id for each BE step (undefined = not surfaced in v1). */
export const STEP_TO_SUBSECTION: Record<OnboardingStepName, SubsectionId | undefined> = {
    use_case_and_intent: "use-case",
    legal_entity: "legal-entity",
    tax_identification: "tax-identification",
    signatory_authority: "signatory-authority",
    beneficial_ownership: "beneficial-ownership",
    sanctions_screening: "sanctions-screening",
    source_of_funds: "source-of-funds",
    wallet_declarations: "wallet-primary",
    msa_signing: "msa",
    minting_agreement_signing: "order-form"
};

/** Mirrors yaas-api `AGREEMENT_PREREQUISITE_STEPS` — what `getStepRequires`
 *  returns for `msa_signing` AND for `minting_agreement_signing`. The two
 *  agreements are independent contracts, so neither lists the other and both
 *  can be out for signature at the same time. */
export const AGREEMENT_STEP_REQUIRES: readonly OnboardingStepName[] = [
    "use_case_and_intent",
    "legal_entity",
    "tax_identification",
    "signatory_authority",
    "beneficial_ownership",
    "source_of_funds",
    "wallet_declarations"
] as const;

/** Applicant-driven steps (excludes admin `sanctions_screening`). */
export const APPLICANT_DRIVEN_STEPS: readonly OnboardingStepName[] = [
    "use_case_and_intent",
    "legal_entity",
    "tax_identification",
    "signatory_authority",
    "beneficial_ownership",
    "source_of_funds",
    "wallet_declarations",
    "msa_signing",
    "minting_agreement_signing"
] as const;

// Shared, so the issuer console titles an applicant's sections the same way.
const STEP_DISPLAY_LABEL: Record<OnboardingStepName, string> = APPLICANT_STEP_DISPLAY_LABEL;

export function stepDisplayLabel(stepName: OnboardingStepName): string {
    return STEP_DISPLAY_LABEL[stepName];
}

export function subsectionIdForStep(stepName: OnboardingStepName): SubsectionId | undefined {
    return STEP_TO_SUBSECTION[stepName];
}

/** Applicant-facing MSA gate list order (matches left-rail `CATEGORY_ORDER`). */
export const MSA_GATE_SUBSECTION_ORDER: readonly SubsectionId[] = [
    "use-case",
    "legal-entity",
    "beneficial-ownership",
    "source-of-funds",
    "wallet-primary",
    "signatory-authority"
] as const;

const MSA_GATE_SUBSECTION_LABEL: Partial<Record<SubsectionId, string>> = {
    "use-case": "Use Case & Intent",
    "legal-entity": "Company",
    "signatory-authority": "Authorized Signatory",
    "beneficial-ownership": "Ultimate Beneficiaries",
    "source-of-funds": "Source of Funds",
    "wallet-primary": "Destination Accounts"
};

/** BE step → rail row (`tax_identification` folds into Company). */
export function gateSubsectionIdForStep(stepName: OnboardingStepName): SubsectionId | undefined {
    if (stepName === "tax_identification") return "legal-entity";
    return subsectionIdForStep(stepName);
}

function stepStatusToSectionStatus(status: OnboardingStepStatus): SectionStatus {
    switch (status) {
        case "not_started":
            return "NotStarted";
        case "in_progress":
            return "InProgress";
        case "pending_external":
            return "WaitingOnSolomon";
        case "complete":
            return "Complete";
        case "rejected":
            return "NeedsAttention";
    }
}

export interface MsaPreconditionGateItem {
    refId: SubsectionId;
    title: string;
    status: SectionStatus;
}

export function buildMsaPreconditionGateItems(
    preconditions:
        | ReadonlyArray<{
              refId: SubsectionId;
              title: string;
              status: SectionStatus;
          }>
        | undefined,
    msaReadiness: StepReadiness | null,
    steps?: Pick<OnboardingStepView, "stepName" | "status" | "payload">[]
): MsaPreconditionGateItem[] {
    const incompleteIds = new Set<SubsectionId>();

    if (msaReadiness && !msaReadiness.satisfied) {
        for (const stepName of msaReadiness.missing) {
            const refId = gateSubsectionIdForStep(stepName);
            if (refId) incompleteIds.add(refId);
        }
    } else {
        for (const p of preconditions ?? []) {
            if (p.refId === "tax-identification") continue;
            if (p.status !== "Complete") incompleteIds.add(p.refId);
        }
    }

    return MSA_GATE_SUBSECTION_ORDER.filter((refId) => incompleteIds.has(refId)).map((refId) => {
        const fromRail = preconditions?.find((p) => p.refId === refId);
        let status = fromRail?.status ?? "NotStarted";

        if (refId === "legal-entity") {
            if (steps) {
                const legal = steps.find((s) => s.stepName === "legal_entity");
                const tax = steps.find((s) => s.stepName === "tax_identification");
                status = rollupCompanySubsectionStatus(
                    legal ? stepStatusToSectionStatus(legal.status) : undefined,
                    tax ? stepStatusToSectionStatus(tax.status) : undefined
                );
            } else {
                const tax = preconditions?.find((p) => p.refId === "tax-identification");
                status = rollupCompanySubsectionStatus(fromRail?.status, tax?.status);
            }
        }

        const title = fromRail?.title ?? MSA_GATE_SUBSECTION_LABEL[refId] ?? refId;

        return { refId, title, status };
    });
}

export type StepReadiness = { satisfied: true } | { satisfied: false; missing: OnboardingStepName[] };

export function beneficialOwnershipEntryCount(payload: Record<string, unknown> | null | undefined): number {
    const entries = (payload as { entries?: unknown[] } | null)?.entries;
    return Array.isArray(entries) ? entries.length : 0;
}

/** The 12 payload fields yaas-api requires before `legal_entity`
 *  completes — mirrors `OnboardingStepService.legalEntityPayloadIsComplete`.
 *  Exported so the form can name what is still missing instead of just
 *  disabling its Save button. */
export const LEGAL_ENTITY_REQUIRED_FIELDS = [
    { field: "legalName", label: "Legal name" },
    { field: "entityType", label: "Entity type" },
    { field: "registeredAddress", label: "Registered address" },
    { field: "companyNumber", label: "Company number" },
    { field: "incorporationJurisdiction", label: "Incorporation jurisdiction" },
    { field: "formationDocumentUuid", label: "Formation document" },
    { field: "operatingDocumentUuid", label: "Operating document" },
    { field: "certificateOfGoodStandingUuid", label: "Certificate of good standing" },
    { field: "primaryContactEmail", label: "Primary contact email" },
    { field: "primaryContactName", label: "Primary contact name" },
    { field: "legalContactEmail", label: "Legal contact email" },
    { field: "legalContactName", label: "Legal contact name" }
] as const;

function hasPayloadString(payload: Record<string, unknown> | null | undefined, field: string): boolean {
    const value = payload?.[field];
    return typeof value === "string" && value.trim().length > 0;
}

export function legalEntityPayloadIsComplete(payload: Record<string, unknown> | null | undefined): boolean {
    return LEGAL_ENTITY_REQUIRED_FIELDS.every(({ field }) => hasPayloadString(payload, field));
}

/**
 * Per-slot completion, derived from the step payload the slot belongs to.
 *
 * The rail used to show a status on section rows only, so a section sitting
 * at "In Progress" because one of its uploads was never made looked
 * identical to one waiting on a field. Slots that declare no `fulfilment`
 * (item-list instances, provider-run flows) have nothing to derive from and
 * return undefined — those rows stay badge-less.
 */
export function slotStatusFromPayload(
    slot: Pick<Slot, "fulfilment">,
    payload: Record<string, unknown> | null | undefined
): SlotStatus | undefined {
    const fulfilment = slot.fulfilment;
    if (!fulfilment) return undefined;
    if (fulfilment.fields.every((field) => hasPayloadString(payload, field))) {
        return "Complete";
    }
    return fulfilment.optional ? "Optional" : "NotStarted";
}

export interface MissingAgreementField {
    field: string;
    label: string;
}

export function getLegalEntityCompletionMissingFields(
    steps: Pick<OnboardingStepView, "stepName" | "payload">[]
): MissingAgreementField[] {
    const legalEntity = steps.find((step) => step.stepName === "legal_entity")?.payload;
    return LEGAL_ENTITY_REQUIRED_FIELDS.filter(({ field }) => !hasPayloadString(legalEntity, field));
}

export function getAgreementRenderMissingFields(
    application: Pick<OnboardingApplicationView, "signatoryEmail" | "signatoryName">,
    steps: Pick<OnboardingStepView, "stepName" | "payload">[],
    agreementType: "msa" | "minting_master"
): MissingAgreementField[] {
    const legalEntity = steps.find((step) => step.stepName === "legal_entity")?.payload;
    const signatory = steps.find((step) => step.stepName === "signatory_authority")?.payload;
    // Older API response schemas removed these application projections during
    // serialization. Use the applicant-owned step payload only when a field is
    // absent from the wire response. An explicit null still means the server
    // has no promoted projection and must keep agreement signing blocked.
    const signatoryEmail =
        application.signatoryEmail === undefined
            ? hasPayloadString(signatory, "signatoryEmail")
                ? (signatory?.signatoryEmail as string)
                : null
            : application.signatoryEmail;
    const signatoryName =
        application.signatoryName === undefined
            ? hasPayloadString(signatory, "signatoryName")
                ? (signatory?.signatoryName as string)
                : null
            : application.signatoryName;
    return [
        ...(!hasPayloadString(legalEntity, "legalName") ? [{ field: "legalName", label: "Legal name" }] : []),
        ...(!hasPayloadString(legalEntity, "entityType") ? [{ field: "entityType", label: "Entity type" }] : []),
        ...(!hasPayloadString(legalEntity, "registeredAddress")
            ? [{ field: "registeredAddress", label: "Registered address" }]
            : []),
        ...(agreementType === "minting_master" && !hasPayloadString(legalEntity, "companyNumber")
            ? [{ field: "companyNumber", label: "Company number" }]
            : []),
        ...(!signatoryEmail ? [{ field: "signatoryEmail", label: "Signatory email" }] : []),
        ...(!signatoryName ? [{ field: "signatoryName", label: "Signatory name" }] : []),
        ...(!hasPayloadString(signatory, "signatoryTitle")
            ? [{ field: "signatoryTitle", label: "Signatory title" }]
            : [])
    ];
}

/**
 * Item-list subsections (Ultimate Beneficiaries, Destination Accounts):
 * empty list and nothing persisted → Not started; draft row only → In progress.
 */
export function itemListSectionStatus(
    mappedStatus: SectionStatus,
    localSlotCount: number,
    persistedCount: number
): SectionStatus {
    if (localSlotCount === 0 && persistedCount === 0) {
        return "NotStarted";
    }
    if (persistedCount === 0 && localSlotCount > 0) {
        return "InProgress";
    }
    return mappedStatus;
}

/** Structured forms: unsaved edits bump Not started / Complete to In progress. */
export function sectionStatusWithDraftActivity(status: SectionStatus, hasDraftActivity: boolean): SectionStatus {
    if (!hasDraftActivity) return status;
    if (status === "NotStarted" || status === "Complete") {
        return "InProgress";
    }
    return status;
}

/** True when the step row is Complete and any step-specific payload rules pass. */
export function isStepEffectivelyComplete(step: Pick<OnboardingStepView, "stepName" | "status" | "payload">): boolean {
    if (step.status !== "complete") return false;
    if (step.stepName === "legal_entity") {
        return legalEntityPayloadIsComplete(step.payload);
    }
    if (step.stepName === "beneficial_ownership") {
        return beneficialOwnershipEntryCount(step.payload) > 0;
    }
    // `wallet_declarations` completes via verify ceremony, not payload
    // entries — effective completion is enforced in the hub from
    // verified-wallet count when rendering + MSA readiness overlays.
    return true;
}

export function getStepReadiness(
    stepName: OnboardingStepName,
    steps: Pick<OnboardingStepView, "stepName" | "status" | "payload">[]
): StepReadiness {
    const requires =
        stepName === "msa_signing" || stepName === "minting_agreement_signing"
            ? AGREEMENT_STEP_REQUIRES
            : ([] as OnboardingStepName[]);
    if (requires.length === 0) return { satisfied: true };

    const stepByName = new Map(steps.map((s) => [s.stepName, s]));
    const missing = requires.filter((req) => {
        const step = stepByName.get(req);
        return !step || !isStepEffectivelyComplete(step);
    });
    if (missing.length === 0) return { satisfied: true };
    return { satisfied: false, missing: [...missing] };
}

export interface ApplicationSubmitReadiness {
    canSubmit: boolean;
    /** Human-readable names for incomplete applicant steps. */
    missingStepLabels: string[];
    /** Shown when the primary CTA should stay disabled. */
    disabledReason: string | null;
}

export function getApplicationSubmitReadiness(
    steps: Pick<OnboardingStepView, "stepName" | "status" | "payload">[],
    applicationStatus: OnboardingApplicationStatus
): ApplicationSubmitReadiness {
    if (applicationStatus !== "draft") {
        return {
            canSubmit: false,
            missingStepLabels: [],
            disabledReason:
                applicationStatus === "in_review"
                    ? "Your application is already submitted for review."
                    : "This application can no longer be edited."
        };
    }

    const stepByName = new Map(steps.map((s) => [s.stepName, s]));
    const applicantSteps = APPLICANT_DRIVEN_STEPS.filter(
        (name) => name !== "minting_agreement_signing" || stepByName.has(name)
    );
    const missing = applicantSteps.filter((name) => {
        const step = stepByName.get(name);
        return !step || !isStepEffectivelyComplete(step);
    });

    if (missing.length === 0) {
        return {
            canSubmit: true,
            missingStepLabels: [],
            disabledReason: null
        };
    }

    return {
        canSubmit: false,
        missingStepLabels: missing.map(stepDisplayLabel),
        disabledReason: "Complete every section before submitting for review."
    };
}
